import React, { useEffect, useRef, useState } from 'react';
import { CLOUD_NAME, UPLOAD_PRESET_STUDY_NOTE, UPLOAD_PRESET_AVATAR } from '../../cloudinaryConfig';

// Define the default array outside the component to ensure referential stability.
const DEFAULT_ACCEPTED_FORMATS = ['jpg', 'jpeg', 'png', 'webp', 'gif'];

const CloudinaryUploadWidget = ({ 
  onUpload, 
  children, 
  cropping = true,
  croppingAspectRatio = 1,
  showSkipCropButton = true,
  maxFileSize = 10000000, // 10MB
  acceptedFormats = DEFAULT_ACCEPTED_FORMATS, // Use the stable constant
  theme = 'minimal',
  uploadType // 'avatar' or 'study_note'
}) => {
  const cloudinaryWidget = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    script.onload = () => {
      cloudinaryWidget.current = window.cloudinary.createUploadWidget(
        {
          cloudName: CLOUD_NAME,
          uploadPreset: uploadType === 'avatar' ? UPLOAD_PRESET_AVATAR : UPLOAD_PRESET_STUDY_NOTE,
          sources: ['local', 'url', 'camera', 'google_drive', 'dropbox'],
          cropping: cropping,
          croppingAspectRatio: croppingAspectRatio,
          multiple: false,
          showSkipCropButton: showSkipCropButton,
          maxFileSize: maxFileSize,
          clientAllowedFormats: acceptedFormats,
          language: 'ja',
          theme: theme,
          // モダンなスタイリング設定
          styles: {
            palette: {
              window: "#FFFFFF",
              windowBorder: "#E5E7EB",
              tabIcon: "#6B7280",
              menuIcons: "#6B7280",
              textDark: "#111827",
              textLight: "#6B7280",
              link: "#3B82F6",
              action: "#FFFFFF", // ボタンテキストを白に変更
              inactiveTabIcon: "#9CA3AF",
              error: "#EF4444",
              inProgress: "#F59E0B",
              complete: "#10B981",
              sourceBg: "#F9FAFB",
              actionBackground: "#3B82F6" // ボタン背景を青に設定
            },
            fonts: {
              default: null,
              "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif": {
                url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap",
                active: true
              }
            },
            frame: {
              background: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)"
            }
          },
          text: {
            ja: {
              menu: {
                files: 'ファイルを選択',
                web: 'ウェブアドレス',
                camera: 'カメラで撮影',
                google_drive: 'Google Drive',
                dropbox: 'Dropbox',
              },
              local: {
                browse: 'ファイルを選択する',
                dd_title_single: 'ここにファイルをドラッグ＆ドロップ',
                drop_title_single: 'ファイルをドロップしてアップロード',
                dd_title_multi: '複数のファイルをドラッグ＆ドロップ',
              },
              url: {
                title: 'ウェブから画像をアップロード',
                input: '画像のURLを入力',
                add_btn: '追加',
              },
              camera: {
                capture_btn: '撮影する',
                cancel_btn: 'キャンセル',
                take_pic_title: '写真を撮る',
                explanation: 'カメラを使って写真を撮影できます',
              },
              crop: {
                title: '画像をトリミング',
                crop_btn: 'トリミング実行',
                skip_btn: 'スキップ',
                reset_btn: 'リセット',
                close_btn: '閉じる',
                close_return: '戻る',
                zoom_in_btn: '拡大',
                zoom_out_btn: '縮小',
                rotate_left_btn: '左に回転',
                rotate_right_btn: '右に回転',
                flip_horizontally_btn: '水平反転',
                flip_vertically_btn: '垂直反転',
              },
              buttons: {
                upload: 'アップロード開始',
                cancel: 'キャンセル',
                close: '閉じる',
                select: 'ファイルを選択',
                back: '戻る',
                advanced: '詳細オプション',
                done: '完了',
                retry: '再試行',
                next: '次へ',
                previous: '前へ',
              },
              progress: {
                uploading: 'アップロード中...',
                processing: '画像を処理中...',
                preparing: '準備中...',
              },
              errors: {
                file_too_large: `ファイルサイズは${Math.floor(maxFileSize / 1000000)}MB以下にしてください`,
                unsupported_format: `対応形式: ${acceptedFormats.join(', ').toUpperCase()}`,
                generic_error: 'エラーが発生しました。再度お試しください。',
                network_error: 'ネットワークエラーです。接続を確認してください。',
              },
              queue: {
                title: 'アップロード待ち',
                title_uploading_with_counter: '{{num}} 個のファイルをアップロード中',
                title_processing_with_counter: '{{num}} 個のファイルを処理中',
                title_uploading_processing_with_counter: '{{num}} 個のファイルをアップロード・処理中',
                title_completed: 'アップロード完了',
                done: 'すべて完了',
                mini_title: 'アップロード済み',
                mini_title_uploading: 'アップロード中',
                show_completed: '完了済みを表示',
                retry_failed: '失敗したファイルを再試行',
                abort_all: 'すべて中止',
                upload_more: 'さらに追加',
              }
            }
          }
        },
        (error, result) => {
          if (error) {
            console.error('Cloudinary upload error:', error);
            setIsUploading(false);
            setUploadProgress(0);
            return;
          }

          if (result && result.event) {
            switch (result.event) {
              case 'upload-added':
                setIsUploading(true);
                setUploadProgress(0);
                break;
              
              case 'progress':
                if (result.info && result.info.progress) {
                  setUploadProgress(result.info.progress);
                }
                break;
              
              case 'success':
                setIsUploading(false);
                setUploadProgress(100);
                
                let imageUrl = result.info.secure_url;
                
                // カスタムクロッピング座標の処理
                if (result.info.coordinates && result.info.coordinates.custom) {
                  const coords = result.info.coordinates.custom[0];
                  const transformation = `x_${coords[0]},y_${coords[1]},w_${coords[2]},h_${coords[3]},c_crop`;
                  const parts = result.info.secure_url.split('upload/');
                  
                  if (parts.length === 2) {
                    imageUrl = `${parts[0]}upload/${transformation}/${parts[1]}`;
                  }
                }
                
                // 追加の画像最適化
                const optimizedUrl = imageUrl.includes('/upload/')
                  ? imageUrl.replace('/upload/', '/upload/f_auto,q_auto,dpr_auto/')
                  : imageUrl;
                
                onUpload(optimizedUrl);
                
                // 少し遅延させてプログレスをリセット
                setTimeout(() => {
                  setUploadProgress(0);
                }, 1000);
                break;
                
              case 'abort':
                setIsUploading(false);
                setUploadProgress(0);
                break;
            }
          }
        }
      );
    };
    
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      // Note: We are not destroying the widget instance here.
      // This can lead to memory leaks if the widget is created multiple times.
      // However, with the dependency array fix, this useEffect should only run once per component mount.
    };
  }, [onUpload, cropping, croppingAspectRatio, showSkipCropButton, maxFileSize, acceptedFormats, theme]);

  const openWidget = (e) => {
    e.preventDefault();
    if (cloudinaryWidget.current) {
      cloudinaryWidget.current.open();
    }
  };

  // プログレス表示用のスタイル
  const progressStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      opacity: isUploading ? 1 : 0,
      visibility: isUploading ? 'visible' : 'hidden',
      transition: 'all 0.3s ease',
    },
    progressCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      minWidth: '300px',
      textAlign: 'center',
    },
    progressBar: {
      width: '100%',
      height: '6px',
      backgroundColor: '#E5E7EB',
      borderRadius: '3px',
      overflow: 'hidden',
      marginTop: '16px',
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#3B82F6',
      borderRadius: '3px',
      width: `${uploadProgress}%`,
      transition: 'width 0.3s ease',
    },
  };

  return (
    <>
      <div onClick={openWidget} style={{ cursor: 'pointer' }}>
        {children}
      </div>
      
      {/* モダンなプログレス表示 */}
      <div style={progressStyles.overlay}>
        <div style={progressStyles.progressCard}>
          <div style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '8px' 
          }}>
            アップロード中...
          </div>
          <div style={{ 
            fontSize: '14px', 
            color: '#6B7280', 
            marginBottom: '16px' 
          }}>
            {uploadProgress}% 完了
          </div>
          <div style={progressStyles.progressBar}>
            <div style={progressStyles.progressFill}></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CloudinaryUploadWidget;